import type { BalanceAmount } from "@shyne/shared/api";
import MainLayout from "@/components/layouts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, CheckCircle2, ExternalLink, RefreshCw, Wallet } from "lucide-react";
import { useStripeConnectSettings } from "@/app/subs/settings/stripe-connect/stripe-connect.hooks";

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
}

export default function IndividualStripeConnectPage() {
  const {
    status,
    loading,
    error,
    action,
    refresh,
    handleConnect,
    handleContinueOnboarding,
    handleOpenDashboard,
    formattedRequirements,
  } = useStripeConnectSettings();

  const isActionLoading = (type: "connect" | "continue" | "dashboard" | "refresh") => action === type;

  const renderHeader = () => (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 className="text-3xl font-semibold">Stripe Connect</h1>
        <p className="text-muted-foreground mt-1">
          Connect your Stripe account to receive payouts for completed work.
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={() => refresh()} disabled={loading || isActionLoading("refresh")}>
        <RefreshCw className="h-4 w-4 mr-2" />
        Refresh
      </Button>
    </div>
  );

  const renderLoadingState = () => (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64" />
      </CardHeader>
      <CardContent className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-20 w-full" />
      </CardContent>
    </Card>
  );

  const renderErrorState = () =>
    error ? (
      <div className="bg-destructive/10 text-destructive p-4 rounded-md flex items-start gap-3">
        <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium">Unable to load Stripe status</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    ) : null;

  const renderNoAccountState = () => (
    <Card>
      <CardHeader>
        <CardTitle>Set up payouts with Stripe</CardTitle>
        <CardDescription>
          Shyne uses Stripe Express to send your earnings. Start onboarding to connect or create your payout account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Wallet className="h-5 w-5" />
          <span>
            You&apos;ll be redirected to Stripe to provide payout details. Once complete, you can receive payments
            directly to your bank account.
          </span>
        </div>
        <Button onClick={() => handleConnect()} disabled={isActionLoading("connect")}>
          {isActionLoading("connect") ? "Connecting…" : "Connect with Stripe"}
        </Button>
      </CardContent>
    </Card>
  );

  const renderPendingState = () => {
    if (status?.status !== "pending") return null;

    return (
      <Card>
        <CardHeader>
          <CardTitle>Complete your Stripe onboarding</CardTitle>
          <CardDescription>Stripe needs additional information before payouts can begin.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">Account ID: {status.stripeAccountId}</Badge>
            <Badge variant="outline">Details submitted: {status.detailsSubmitted ? "Yes" : "No"}</Badge>
          </div>

          <div>
            <p className="font-medium mb-2">Outstanding requirements</p>
            {formattedRequirements.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Stripe is processing your submission. Check back shortly or reopen the onboarding flow.
              </p>
            ) : (
              <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                {formattedRequirements.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex gap-3">
            <Button onClick={() => handleContinueOnboarding()} disabled={isActionLoading("continue")}>
              {isActionLoading("continue") ? "Opening…" : "Continue onboarding"}
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
            <Button variant="outline" onClick={() => refresh()} disabled={isActionLoading("refresh")}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Check status
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderActiveState = () => {
    if (status?.status !== "active") return null;

    const { balance } = status;

    const renderBalanceList = (label: string, entries: BalanceAmount[]) => (
      <div>
        <p className="font-medium mb-2">{label}</p>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No {label.toLowerCase()} at this time.</p>
        ) : (
          <ul className="space-y-1 text-sm text-muted-foreground">
            {entries.map((entry, index) => (
              <li key={`${label}-${entry.currency}-${index}`}>
                {formatCurrency(entry.amount, entry.currency)} ({entry.currency.toUpperCase()})
              </li>
            ))}
          </ul>
        )}
      </div>
    );

    return (
      <Card>
        <CardHeader>
          <CardTitle>Your Stripe account is active</CardTitle>
          <CardDescription>Manage payouts and account details directly in Stripe.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Charges enabled
            </Badge>
            <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Payouts enabled
            </Badge>
            <Badge variant="secondary">Account ID: {status.stripeAccountId}</Badge>
          </div>

          {balance === null ? (
            <p className="text-sm text-muted-foreground">Balances are unavailable right now.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {renderBalanceList("Available balance", balance.available)}
              {renderBalanceList("Pending balance", balance.pending)}
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={() => handleOpenDashboard()} disabled={isActionLoading("dashboard")}>
              {isActionLoading("dashboard") ? "Opening…" : "Open Stripe Dashboard"}
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
            <Button variant="outline" onClick={() => refresh()} disabled={isActionLoading("refresh")}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh balances
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const breadcrumb = [{ name: "Stripe Connect", href: "#" }];

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <div className="container mx-auto py-6 space-y-6">
        {renderHeader()}
        {renderErrorState()}
        {loading && renderLoadingState()}
        {!loading && status?.status === "no_account" && renderNoAccountState()}
        {!loading && status?.status === "pending" && renderPendingState()}
        {!loading && status?.status === "active" && renderActiveState()}
      </div>
    </MainLayout>
  );
}


